import React, { forwardRef, useImperativeHandle } from "react";
import {
  FormControl,
  FormGroup,
  Grid,
  Box,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { setProfile } from "../../../../store/slices/doctorSlice";
import { EMPTY_UUID } from "../../../../constants/appConstants";
import { appColors } from "../../../../constants/theme";
import {
  getAttributeItemList,
  getDoctorAttributeItems,
  getItemIdFromAttrItem,
  getSelectedAttributeItemId,
  isPracticeAreaAttribute,
} from "../professionPayloadUtils";
import { stepTitleSx } from "../style";
import { getDisplayText } from "../../../../utils/professionDisplay";

const isValidAttrId = (id) => id != null && id !== "" && String(id).toLowerCase() !== EMPTY_UUID.toLowerCase();

const sameAttrId = (a, b) => a != null && b != null && String(a).toLowerCase() === String(b).toLowerCase();

const SpecialtyAttributeStep = forwardRef(({ specialtyAttribute, onSuccess, onLoadingChange, initialValues, step }, ref) => {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const profile = useSelector((state) => state.doctor.profile);
  const formData = initialValues || profile;

  const validItemList = getAttributeItemList(specialtyAttribute).filter((item) =>
    isValidAttrId(getItemIdFromAttrItem(item))
  );
  const savedItems = getDoctorAttributeItems(formData);
  const initialSelected = validItemList
    .filter((item) =>
      savedItems.some(
        (sel) => isValidAttrId(getSelectedAttributeItemId(sel)) && sameAttrId(getSelectedAttributeItemId(sel), getItemIdFromAttrItem(item))
      )
    )
    .map((item) => getItemIdFromAttrItem(item));

  const nItems = validItemList.length;
  const isPracticeArea = isPracticeAreaAttribute(specialtyAttribute);
  const effectiveMin = isPracticeArea ? 1 : 0;
  const effectiveMax = nItems;
  const selectionUnlimited = true;

  const formik = useFormik({
    initialValues: {
      selectedItems: initialSelected,
    },
    validationSchema: Yup.object({
      selectedItems: Yup.array()
        .min(
          effectiveMin,
          isPracticeArea
            ? "Onay için en az bir çalışma / hastalık alanı seçmelisiniz."
            : undefined
        )
        .max(nItems, `En fazla ${nItems} seçenek işaretlenebilir`),
    }),
    onSubmit: (values) => {
      if (onSuccess) {
        const currentData = initialValues || profile;
        let allItems = [...getDoctorAttributeItems(currentData)];

        allItems = allItems.filter((item) => isValidAttrId(getSelectedAttributeItemId(item)));
        const currentAttributeItemIds = validItemList.map((item) => getItemIdFromAttrItem(item));
        allItems = allItems.filter((item) => {
          const itemId = getSelectedAttributeItemId(item);
          return !currentAttributeItemIds.some((cid) => sameAttrId(cid, itemId));
        });
        const newItems = values.selectedItems
          .filter((id) => isValidAttrId(id))
          .map((id) => ({ specialtyAttributeItemId: id }));

        onSuccess({
          doctorSpecialtyAttributeItems: [...allItems, ...newItems],
        });
      }
    },
    enableReinitialize: true,
  });

  React.useEffect(() => {
    const currentData = initialValues || profile;
    const currentAttrs = getDoctorAttributeItems(currentData);
    const updatedSelected = validItemList
      .filter((item) =>
        currentAttrs.some(
          (sel) => isValidAttrId(getSelectedAttributeItemId(sel)) && sameAttrId(getSelectedAttributeItemId(sel), getItemIdFromAttrItem(item))
        )
      )
      .map((item) => getItemIdFromAttrItem(item));

    const currentSelected = formik.values.selectedItems || [];
    if (JSON.stringify([...updatedSelected].sort()) !== JSON.stringify([...currentSelected].sort())) {
      formik.setValues({
        selectedItems: updatedSelected
      }, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues, profile, specialtyAttribute]);

  useImperativeHandle(ref, () => ({
    handleSubmit: formik.handleSubmit,
  }));

  const selectedOnThisPage = formik.values.selectedItems || [];

  const updateProfileAttributeItems = (id, checked) => {
    if (!isValidAttrId(id)) return;
    const currentData = initialValues || profile;
    let items = getDoctorAttributeItems(currentData).filter((i) => isValidAttrId(getSelectedAttributeItemId(i)));
    if (checked) {
      if (!items.some((i) => sameAttrId(getSelectedAttributeItemId(i), id))) {
        items.push({ specialtyAttributeItemId: id });
      }
    } else {
      items = items.filter((i) => !sameAttrId(getSelectedAttributeItemId(i), id));
    }
    dispatch(setProfile({ ...currentData, doctorSpecialtyAttributeItems: items }));
  };

  if (!specialtyAttribute) return <div>Veri yüklenemedi.</div>;

  const selectedIdSet = new Set(selectedOnThisPage.map((id) => String(id).toLowerCase()));

  return (
    <Box>
      <form onSubmit={formik.handleSubmit}>
        <FormControl
          component="fieldset"
          error={formik.touched.selectedItems && Boolean(formik.errors.selectedItems)}
          fullWidth
        >
          {(specialtyAttribute.attributeDefinition || specialtyAttribute.attributeDescription) && (
            <Box sx={{ mb: 2 }}>
              {specialtyAttribute.attributeDefinition && (
                <Typography
                  variant="h6"
                  sx={{
                    ...stepTitleSx,
                    mb: specialtyAttribute.attributeDescription ? 1 : 2,
                  }}
                >
                  {specialtyAttribute.attributeDefinition}
                </Typography>
              )}
              {specialtyAttribute.attributeDescription && (
                <Typography
                  variant="body1"
                  sx={{
                    color: appColors.gray500,
                    textAlign: 'left',
                    lineHeight: 1.6,
                    fontSize: '0.95rem',
                    mb: 2,
                  }}
                >
                  {specialtyAttribute.attributeDescription}
                </Typography>
              )}
            </Box>
          )}

          <FormGroup>
            <Grid container spacing={3} justifyContent="space-between">
              {validItemList.map((item) => {
                const itemId = getItemIdFromAttrItem(item);
                const isSelected = selectedIdSet.has(String(itemId).toLowerCase());
                return (
                <Grid size={{ xs: 12, sm: 6, md: 5.8 }} key={itemId}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 2.5,
                      borderRadius: '12px',
                      border: '1px solid',
                      borderColor: isSelected ? appColors.primary : appColors.gray200,
                      backgroundColor: isSelected ? 'rgba(13, 148, 136, 0.08)' : appColors.white,
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      height: '100%',
                      '&:hover': {
                        backgroundColor: isSelected
                          ? 'rgba(13, 148, 136, 0.12)'
                          : appColors.gray50,
                        borderColor: isSelected ? appColors.primaryDark : appColors.primary,
                        transform: 'translateY(-2px)',

                      },
                    }}
                    onClick={() => {
                      if (isSelected) {
                        const newArr = selectedOnThisPage.filter((id) => !sameAttrId(id, itemId));
                        formik.setFieldValue("selectedItems", newArr);
                        updateProfileAttributeItems(itemId, false);
                      } else if (selectionUnlimited || selectedOnThisPage.length < effectiveMax) {
                        const newArr = [...selectedOnThisPage, itemId];
                        formik.setFieldValue("selectedItems", newArr);
                        updateProfileAttributeItems(itemId, true);
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '4px',
                        border: '2px solid',
                        borderColor: isSelected ? appColors.primary : appColors.gray200,
                        backgroundColor: isSelected ? appColors.primary : 'transparent',
                        mr: 2,
                        flexShrink: 0,
                        position: 'relative',
                        '&::after': isSelected ? {
                          content: '""',
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: 12,
                          height: 12,
                          backgroundImage: 'url("data:image/svg+xml,%3csvg viewBox=\'0 0 16 16\' fill=\'white\' xmlns=\'http://www.w3.org/2000/svg\'%3e%3cpath d=\'m13.854 3.646-7.5 7.5a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6 10.293l7.146-7.147a.5.5 0 0 1 .708.708z\'/%3e%3c/svg%3e")',
                          backgroundSize: 'contain',
                          backgroundRepeat: 'no-repeat',
                        } : {}
                      }}
                    />

                    <Typography
                      sx={{
                        fontWeight: 500,
                        color: isSelected ? appColors.primary : appColors.gray900,
                        fontSize: '0.95rem',
                        flex: 1,
                        textAlign: 'left',
                        lineHeight: 1.4,
                        userSelect: 'none'
                      }}
                    >
                      {getDisplayText(item, i18n.language, "itemText", "translate") ||
                        item.itemText ||
                        item.ItemText ||
                        ""}
                    </Typography>
                  </Box>
                </Grid>
              );
              })}
            </Grid>
          </FormGroup>

          {formik.touched.selectedItems && formik.errors.selectedItems && (
            <Box sx={{ color: "error.main", mt: 2, textAlign: 'center' }}>
              <Typography sx={{ color: appColors.errorDark, fontSize: '0.875rem' }}>
                {formik.errors.selectedItems}
              </Typography>
            </Box>
          )}

          <Box sx={{
            mt: 3,
            p: 3,
            backgroundColor: appColors.warningBg,
            borderRadius: '12px',
            border: `1px solid ${appColors.warningBg}`,

          }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}>
                <Box sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  backgroundColor: appColors.warningBg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: appColors.warningDark,
                }}>
                  i
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    color: appColors.warningDark,
                    fontSize: '0.875rem'
                  }}
                >
                  <span style={{ fontWeight: 700 }}>Önemli Not:</span>{' '}
                  {isPracticeArea
                    ? 'Onay için en az bir çalışma / hastalık alanı seçmelisiniz.'
                    : selectionUnlimited
                    ? 'İstediğiniz kadar seçenek işaretleyebilirsiniz (zorunlu seçim yok).'
                    : `En az ${effectiveMin}, en fazla ${effectiveMax} seçim yapabilirsiniz.`}
                </Typography>
              </Box>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 700,
                  color: appColors.warningDark,
                  fontSize: '0.875rem'
                }}
              >
                Seçilen: {selectedOnThisPage.length}
                {!selectionUnlimited && `/${effectiveMax}`}
              </Typography>
            </Box>
          </Box>
        </FormControl>
      </form>
    </Box>
  );
});

export default SpecialtyAttributeStep;
